import React, { useEffect, useState } from 'react';
import loadable from '@loadable/component';
import axios from 'axios';

import withPageComponents from '../with-page-components';
import { MetricsObserver } from '../../../metrics/event-observer';
import { useTemplateKey } from '../../../hooks/usePageComponents';
import { getCTOLink } from '../../../core/src/etr-api-client/cto';
import { Helpers } from '../../../core';
import { StoreAppAPI } from '../../../core';

const storeAppApi = new StoreAppAPI();

const CartPage = loadable(() => Helpers.retryFunc(() => import('../../../cart/cart-page-container')));
const PDPPage = loadable(() => Helpers.retryFunc(() => import('../../../pdp/components-v2')));

export const usePrefetchMinfest = (key, fetch) => {
    const [manifest, setManifest] = useState();
    const { prefetch } = manifest || {};
    useEffect(() => {
        storeAppApi.component.get([`prefetchManifest${key ? `:${key}` : ''}`]).then(resp => {
            const [manifest] = resp?.data || [];
            setManifest(manifest?.value);
        });
    }, []);

    useEffect(() => {
        if (fetch && prefetch?.length > 0) {
            prefetchPage(prefetch, 'prefetch');
        }
    }, [prefetch, fetch]);

    return { prefetchManifest: manifest };
};

const prefetchPage = (urls, rule = 'prefetch', as) => {
    try {
        if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules') && !as) {
            //remove existing speculation rules
            document.querySelectorAll('script[type="speculationrules"]').forEach(script => script.remove());
            const specScript = document.createElement('script');
            specScript.type = 'speculationrules';
            const specRules = {
                [rule]: [
                    {
                        source: 'list',
                        urls,
                    },
                ],
            };
            specScript.textContent = JSON.stringify(specRules);
            document.body.append(specScript);
        } else {
            //remove existing prefetch links
            document.querySelectorAll('link[data-prefetch]').forEach(link => link.remove());

            urls.forEach(href => {
                const linkElem = document.createElement('link');
                linkElem.dataset.prefetch = 'true';
                linkElem.rel = rule;
                linkElem.href = href;
                if (as) {
                    linkElem.as = as;
                }
                document.head.append(linkElem);
            });
        }
    } catch (e) {
        console.log(e);
    }
};

let prefetchCartOnce = false;
const useOnAddToCart = (addToCartManifest, enablePrePagePrefetch) => {
    useEffect(() => {
        if (!enablePrePagePrefetch && (addToCartManifest || []).length < 1) {
            return () => {};
        }
        const onAddToCart = event => {
            if (!prefetchCartOnce && addToCartManifest?.length > 0) {
                prefetchCartOnce = true;
                prefetchPage(addToCartManifest, 'prefetch');
            }
        };
        MetricsObserver.subscribe('e_addToCart', onAddToCart);
        return () => {
            MetricsObserver.unsubscribe('e_addToCart', onAddToCart);
        };
    }, [addToCartManifest, enablePrePagePrefetch]);
};

const vanityUrlMap = {};

const usePrefetchCartBundle = () => {
    const [fetchCartBundle, setFetchCartBundle] = useState(false);
    useEffect(() => {
        MetricsObserver.subscribe('e_addToCart', () => {
            setFetchCartBundle(true);
        });
        return () => {
            MetricsObserver.unsubscribe('e_addToCart');
        };
    }, []);
    useEffect(() => {
        if (fetchCartBundle) {
            CartPage.preload();
        }
    }, [fetchCartBundle]);
};

const usePrefetchPDP = () => {
    const [fetchPDP, setFetchPDP] = useState(false);
    const templateKey = useTemplateKey();
    useEffect(() => {
        if (['slp', 'mdp', 'vwa', 'srp'].includes(templateKey)) {
            setFetchPDP(true);
        }
    }, [templateKey]);
    useEffect(() => {
        if (fetchPDP) {
            PDPPage.preload();
        }
    }, [fetchPDP]);
};

/***
 * Performance optimzation function for prefetching likely next pages
 */
export default withPageComponents(
    ({ siteConfig, vanityUrl, productTab, productInitial, isClientGQLLoading }) => {
        const { enablePrePagePrefetch = true } = siteConfig;
        const { catentryId, product_type, shadowCTOSkus } = productInitial || {};
        // const { prefetchManifest } = usePrefetchMinfest();
        //useOnAddToCart(prefetchManifest?.addToCart, enablePrePagePrefetch);
        useEffect(() => {
            const [dir, slug] = vanityUrl?.split('/') || [];
            const baseUrl = `/${dir}/${slug}`;
            //if the vanityUrl is already in the map, dont perform any prefetching
            if (!(baseUrl in vanityUrlMap) && enablePrePagePrefetch && !isClientGQLLoading) {
                vanityUrlMap[baseUrl] = true;

                if (vanityUrl?.indexOf('slp') > -1) {
                    prefetchSLPUrls(productTab);
                }
                //if ConfigureView prefetch accories attach API
                /*
                //doesn't work right now because we dont know how to get the attach url ahead of add to cart
                if (vanityUrl?.indexOf('configure') > -1) {
                    const accessoriesUrl = `/wcs/resources/store/10151/component/cto/accessoriesattach?langId=-1&storeId=10151&catalogId=10051&catEntryId=3074457345620773822&configCatentryId=3074457345620773827&responseFormat=json`;
                    prefetchPage([accessoriesUrl]);
                }*/
                //if productIntial productType is CTO, prefetch CTO page
                if (product_type === 'CTO' && catentryId) {
                    const ctoUrl = getCTOLink(catentryId, 1);
                    prefetchPage([ctoUrl], 'prefetch', 'document');
                }
            }

            return () => {};
        }, [vanityUrl, catentryId, product_type, productTab, isClientGQLLoading]);

        /**If page has shadowCTOSkus prefetch them to improve inp */
        useEffect(() => {
            if (shadowCTOSkus && Object.keys(shadowCTOSkus).length > 0 && enablePrePagePrefetch) {
                const shadowCTOUrls = getDistinctShadowCTOPath(shadowCTOSkus);
                prefetchPage(shadowCTOUrls, 'prefetch', 'document');
            }
        }, [shadowCTOSkus]);
        usePrefetchCartBundle();
        usePrefetchPDP();

        return null;
    },
    { siteConfig: true, components: ['productTab', 'productInitial'], gqlClientLoadStatus: true },
);

//function that gets all distinct path from urls after 'pdp' of the object
function getDistinctShadowCTOPath(obj) {
    let result = [];
    for (const key in obj) {
        for (const key2 in obj[key]) {
            //check url from standard shadow CTO or nested shadow CTO
            const fullUrl = obj[key].url || obj[key][key2].url;
            let path = (fullUrl || '').split('pdp/')[1];
            if (path && !result.includes(path)) {
                result.push(`/us-en/shop/app/api/web/graphql/page/pdp%2F${path}/sync`);
            }
        }
    }
    return result;
}

//
const prefetchSLPUrls = productTab => {
    //loop through productGroups within productTab and construct API urls, ignoring the active productGroup
    const urls = productTab.productGroups.reduce((agg, { key, active }) => {
        if (!active) {
            agg.push(`/us-en/shop/app/api/web/product-group/${encodeURIComponent(key)}?merged=1`);
        }
        return agg;
    }, []);

    prefetchPage(urls);
};
